// Utility functions for exporting reports

// Export data to Excel format (CSV)
export const exportToExcel = (data: any[], filename: string) => {
  // Convert data to CSV format
  if (data.length === 0) {
    alert('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvContent = [
    headers.join(','), // Header row
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Handle values that contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value || '';
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Export data to PDF format (simple HTML-based)
export const exportToPDF = (data: any[], filename: string, title: string) => {
  // Create a simple HTML table for PDF generation
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        table { border-collapse: collapse; width: 100%; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; }
        h1 { color: #333; }
        .summary { margin: 20px 0; padding: 15px; background-color: #f9f9f9; border-radius: 5px; }
      </style>
    </head>
    <body>
      <h1>${title}</h1>
      <div class="summary">
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        <p><strong>Total Records:</strong> ${data.length}</p>
      </div>
      ${data.length > 0 ? `
        <table>
          <thead>
            <tr>
              ${Object.keys(data[0]).map(key => `<th>${key}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${data.map(row => 
              `<tr>${Object.values(row).map(value => `<td>${value || ''}</td>`).join('')}</tr>`
            ).join('')}
          </tbody>
        </table>
      ` : '<p>No data available</p>'}
    </body>
    </html>
  `;

  // Open in new window for printing/saving as PDF
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    // Auto-print after a short delay
    setTimeout(() => {
      newWindow.print();
    }, 500);
  }
};

// Format data for export based on report type
export const formatDataForExport = (reportData: any, reportType: string) => {
  switch (reportType) {
    case 'Project Reports':
      return reportData.data?.map((project: any) => ({
        'Project ID': project.id || project._id,
        'Project Name': project.name,
        'Status': project.status,
        'Type': project.type,
        'Total Budget': project.budget?.total || 0,
        'Materials Budget': project.budget?.materials || 0,
        'Labor Budget': project.budget?.labor || 0,
        'Equipment Budget': project.budget?.equipment || 0,
        'Sub-Contracts Budget': project.budget?.subContracts || 0,
        'Others Budget': project.budget?.others || 0,
        'Start Date': project.startDate ? new Date(project.startDate).toLocaleDateString() : '',
        'End Date': project.endDate ? new Date(project.endDate).toLocaleDateString() : '',
        'Coordinator': project.coordinator?.name || project.coordinator || ''
      })) || [];

    case 'Financial Report':
      const payments = reportData.data?.payments || [];
      const expenses = reportData.data?.expenses || [];
      
      const formattedPayments = payments.map((payment: any) => ({
        'Type': 'Payment',
        'ID': payment.id || payment._id,
        'Project': payment.projectName || payment.projectId,
        'Amount': payment.amount,
        'Currency': payment.currency,
        'Date': payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : '',
        'Method': payment.paymentMethod,
        'Description': payment.description
      }));

      const formattedExpenses = expenses.map((expense: any) => ({
        'Type': 'Expense',
        'ID': expense.id || expense._id,
        'Project': expense.projectName || expense.projectId,
        'Category': expense.category,
        'Amount': expense.amount,
        'Currency': expense.currency,
        'Date': expense.date ? new Date(expense.date).toLocaleDateString() : '',
        'Supplier': expense.supplier,
        'Description': expense.description,
        'Status': expense.status
      }));

      return [...formattedPayments, ...formattedExpenses];

    case 'Fund Request Report':
      return reportData.data?.map((request: any) => ({
        'Request ID': request.id || request._id,
        'Project': request.projectName || request.projectId,
        'Requester': request.requester?.name || request.requester,
        'Amount': request.amount,
        'Currency': request.currency,
        'Status': request.status,
        'Category': request.category,
        'Description': request.description,
        'Submitted Date': request.submittedAt ? new Date(request.submittedAt).toLocaleDateString() : '',
        'Approved Date': request.approvedAt ? new Date(request.approvedAt).toLocaleDateString() : ''
      })) || [];

    case 'Procurement Report':
      return reportData.data?.map((expense: any) => ({
        'Expense ID': expense.id || expense._id,
        'Project': expense.projectName || expense.projectId,
        'Category': expense.category,
        'Amount': expense.amount,
        'Currency': expense.currency,
        'Date': expense.date ? new Date(expense.date).toLocaleDateString() : '',
        'Supplier': expense.supplier,
        'Receipt Type': expense.receiptType,
        'Status': expense.status,
        'Description': expense.description
      })) || [];

    default:
      return reportData.data || [];
  }
};
